import { Target, Users, TrendingUp } from 'lucide-react'
import { COMPETITION_POLICY_NOTICE } from '../utils/competitions'

interface AboutPageProps {
  onLoginClick: () => void
  onExplore: () => void
}

export default function AboutPage({ onLoginClick, onExplore }: AboutPageProps) {
  return (
    <div className="min-h-screen bg-[#050910] text-white">
      <div className="max-w-4xl mx-auto px-4 py-20 space-y-24">
        {/* Hero Section */}
        <section className="text-center space-y-6">
          <h1 className="text-5xl md:text-6xl font-display font-bold bg-gradient-to-r from-white via-white to-green-500 bg-clip-text text-transparent">
            Where Players Own Their Football Careers.
          </h1>
          <p className="text-xl text-slate-200 max-w-2xl mx-auto leading-relaxed">
            Scouta is a Nigerian-built football ecosystem connecting players, scouts, and clubs. Players manage their careers. Scouts discover talent. Clubs recruit with confidence.
          </p>
          <div className="mx-auto max-w-3xl rounded-3xl border border-green-500/20 bg-green-500/10 px-6 py-5 text-left text-slate-100">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-green-300">Data integrity rule</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-200">
              {COMPETITION_POLICY_NOTICE}
            </p>
          </div>
        </section>

        {/* Vision & Mission Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="surface-panel p-8 rounded-3xl border border-white/10 space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-green-500/10 flex items-center justify-center text-green-500">
              <Target className="w-6 h-6" />
            </div>
            <h3 className="text-2xl font-bold">Our Mission</h3>
            <p className="text-slate-200 leading-relaxed">
              To give players control over their football careers while providing Scouts and Clubs with the tools to discover, evaluate, and recruit talent effectively.
            </p>
          </div>
          <div className="surface-panel p-8 rounded-3xl border border-white/10 space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500">
              <TrendingUp className="w-6 h-6" />
            </div>
            <h3 className="text-2xl font-bold">Our Vision</h3>
            <p className="text-slate-200 leading-relaxed">
              To become the trusted football ecosystem for African talent — connecting players with opportunities, Scouts with data, and Clubs with the right recruits.
            </p>
          </div>
        </div>

        {/* Why Scouta? */}
        <section className="space-y-12">
          <div className="text-center space-y-3">
            <h2 className="text-3xl font-display font-bold">Why We Built Scouta</h2>
            <div className="h-1 w-20 bg-green-500 mx-auto rounded-full" />
          </div>
          
          <div className="grid gap-12">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-1 space-y-4">
                <h3 className="text-2xl font-bold text-primary">The Player Gap</h3>
                <p className="text-slate-200">
                  Most football platforms focus on scouts and clubs. Players have no centralized way to manage their careers, showcase their achievements, or connect with opportunities. Scouta puts players first.
                </p>
              </div>
              <div className="flex-1 surface-panel p-6 rounded-3xl border border-white/10 bg-[#0a151f] w-full aspect-video flex items-center justify-center">
                 <Users className="w-20 h-20 text-slate-700" />
              </div>
            </div>

            <div className="flex flex-col md:flex-row-reverse gap-8 items-center">
            <div className="flex-1 space-y-4">
              <h3 className="text-2xl font-bold text-primary">Connected Ecosystem</h3>
              <p className="text-slate-200">
                Players manage profiles, stats, clips, and documents. Scouts discover talent through advanced search and filters. Clubs recruit with trial invitations and direct messaging. Everyone benefits from a single source of truth.
              </p>
            </div>
              <div className="flex-1 surface-panel p-6 rounded-3xl border border-white/10 bg-[#0a151f] w-full aspect-video flex items-center justify-center">
                 <img src="/scoutang-logo.svg" alt="Scouta" className="w-20 h-20 opacity-20" />
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="surface-bg rounded-[40px] p-12 border border-white/5 text-center space-y-8">
          <h2 className="text-3xl font-bold">Join the Ecosystem.</h2>
          <p className="text-slate-200 max-w-xl mx-auto">
            Whether you're a player building your career, a Scout discovering talent, or a Club recruiting — Scouta is built for you.
          </p>
           <div className="flex flex-wrap justify-center gap-4">
            <button 
              onClick={onLoginClick}
              className="btn-primary px-8 py-4"
            >
              Get Started
            </button>
            <button 
              onClick={onExplore}
              className="btn-secondary px-8 py-4"
            >
              Explore Players
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}
